/**
 * Suivi de la progression le long d'une trace.
 *
 * Portage de `SnappedPositionTracker.kt`. Deux rôles :
 *
 *  - **mémoriser la progression** pour lever l'ambiguïté sur les boucles et
 *    les aller-retours, où un même point de l'écran correspond à plusieurs
 *    points kilométriques ;
 *  - **mémoïser** le dernier calcul, car la position est interrogée par
 *    plusieurs panneaux à chaque tick et la projection coûte cher sur une
 *    trace de plusieurs centaines de milliers de points.
 */

import type { GpxTrack } from './track'

/**
 * Persistance de l'indice de progression, par trace.
 *
 * Séparée du suivi lui-même pour rester testable et pour survivre à un
 * rechargement de page : reprendre une sortie interrompue au bon kilomètre
 * évite de repartir sur la mauvaise itération d'une boucle.
 */
export interface ProgressionHintStore {
  get(trackId: string): number | null
  set(trackId: string, km: number): void
  clear(trackId: string): void
}

/** Implémentation adossée à `localStorage`. */
export class LocalProgressionHintStore implements ProgressionHintStore {
  constructor(private readonly prefix = 'velogpx.progression.') {}

  get(trackId: string): number | null {
    try {
      const raw = localStorage.getItem(this.prefix + trackId)
      if (raw === null) return null
      const value = Number.parseFloat(raw)
      return Number.isFinite(value) ? value : null
    } catch {
      return null
    }
  }

  set(trackId: string, km: number): void {
    try {
      localStorage.setItem(this.prefix + trackId, String(km))
    } catch {
      // Stockage indisponible : la progression reste valable pour la session.
    }
  }

  clear(trackId: string): void {
    try {
      localStorage.removeItem(this.prefix + trackId)
    } catch {
      // Sans effet.
    }
  }
}

/** Store en mémoire, pour les tests et les environnements sans stockage. */
export class MemoryProgressionHintStore implements ProgressionHintStore {
  #values = new Map<string, number>()

  get(trackId: string): number | null {
    return this.#values.get(trackId) ?? null
  }

  set(trackId: string, km: number): void {
    this.#values.set(trackId, km)
  }

  clear(trackId: string): void {
    this.#values.delete(trackId)
  }
}

export class SnappedPositionTracker {
  /**
   * Progression connue, en kilomètres **bruts** depuis le début de la trace.
   *
   * Volontairement pas dans la convention des marqueurs : cette valeur sert au
   * calcul géométrique, pas à l'affichage, et un décalage de départ réel n'a
   * rien à y faire.
   */
  #lastKnownKm: number | null = null

  #cacheKey: string | null = null
  #cacheValue: number | null = null

  constructor(private readonly hints: ProgressionHintStore) {}

  get lastKnownKm(): number | null {
    return this.#lastKnownKm
  }

  /**
   * Indice de progression connu pour une trace, en kilomètres bruts.
   *
   * Ce que la session courante sait d'abord, ce qui a été enregistré ensuite.
   * C'est ce second cas qui rend la reprise possible après un rechargement de
   * page : sans lui, l'accrochage repartirait sans repère et pourrait se
   * verrouiller sur la mauvaise itération d'une boucle.
   */
  hintFor(trackId: string): number | null {
    return this.#lastKnownKm ?? this.hints.get(trackId)
  }

  /**
   * Abscisse curviligne de la position sur la trace, en mètres.
   *
   * @returns `null` si la position est hors de portée de la trace.
   */
  distanceAtPoint(track: GpxTrack, lat: number, lon: number): number | null {
    const key = `${track.id}:${lat}:${lon}`
    if (key === this.#cacheKey) {
      return this.#cacheValue
    }

    const hintKm = this.#lastKnownKm ?? this.hints.get(track.id)

    let result = track.distanceAtPoint(
      lat,
      lon,
      hintKm === null ? null : hintKm * 1000,
    )

    // L'indice a été refusé — typiquement après une longue sortie de trace,
    // où il désigne un point trop éloigné. On repart sans lui plutôt que de
    // rester bloqué sans position.
    if (result === null && hintKm !== null) {
      this.#lastKnownKm = null
      result = track.distanceAtPoint(lat, lon)
    }

    if (result !== null) {
      this.#lastKnownKm = result / 1000
      this.hints.set(track.id, this.#lastKnownKm)
    }

    this.#cacheKey = key
    this.#cacheValue = result
    return result
  }

  /**
   * Force la progression, après une correction manuelle de position.
   *
   * @param rawKm Kilomètres bruts depuis le début de la trace.
   */
  fixPosition(trackId: string, rawKm: number): void {
    this.#lastKnownKm = rawKm
    this.hints.set(trackId, rawKm)
    this.invalidateCache()
  }

  /**
   * Vide la mémoïsation.
   *
   * La clé de cache ne porte que sur la position : un changement d'indice ne
   * l'invaliderait pas tout seul, alors qu'il change le résultat.
   */
  invalidateCache(): void {
    this.#cacheKey = null
    this.#cacheValue = null
  }

  /** Oublie la progression, par exemple à la suppression de la trace. */
  reset(trackId?: string): void {
    this.#lastKnownKm = null
    this.invalidateCache()
    if (trackId !== undefined) {
      this.hints.clear(trackId)
    }
  }
}
